/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useMemo, useState } from "react";

import { useLocation, useParams } from "react-router-dom";
import useStateRef from "react-usestateref";

import calculateOffset from "utils/calculateOffset";
import countPagesNumber from "utils/countPagesNumber";
import isUndefined from "utils/isUndefined";
import normalize from "utils/normalize";
import { safeAssign } from "utils/safeObject";
import stringifer from "utils/stringifer";

import useFetcher from "./useFetcher";

import { useGeneralState } from "state";
import Pagination from "components/ui/Pagination";
import Text from "components/ui/Text";

const extractInfo = (data, range) => {
  const { count: total, number: offsetLength, ...rest } = data;
  const numOfPages = countPagesNumber(total, range);
  return {
    total: total || rest.total,
    offsetLength: offsetLength || rest.offsetLength,
    pages: numOfPages || rest.pages,
  };
};

export const extractNestedKey = (object, key, extraKey, maxDepth = 3) => {
  let data = object;
  if (!data) return data;
  let depth = 0;
  const MAX_DEPTH = Number.isNaN(maxDepth) ? 3 : normalize(maxDepth);
  const hasKey = key && data.hasOwnProperty(key);
  const hasExtraKey = extraKey && data.hasOwnProperty(extraKey);
  while (data && (hasKey || hasExtraKey) && MAX_DEPTH > depth) {
    depth += 1;

    if (isUndefined(data[key]) === false) {
      data = data[key];
    }

    if (isUndefined(data[extraKey]) === false) {
      data = data[extraKey];
    }
  }

  return data;
};

const dataShaper = (data, shapeData, range, fieldName) => {
  // count, number are omitted.
  const { ...rest } = data;
  const paginationInfo = extractInfo(data, range);
  let dataField = rest;
  if (fieldName) {
    if (rest.hasOwnProperty(fieldName)) {
      dataField = rest[fieldName];
    }
  }

  const lengthBeforeShaping = dataField.length;
  dataField = shapeData(dataField);
  // for example, if the data had been filtered in the shapeData and the length is not equal
  // then we need to update the offsetLength and total.
  if (Array.isArray(dataField) && dataField.length !== lengthBeforeShaping) {
    const lengthAfterShaping = dataField.length;
    const diff = Math.abs(lengthAfterShaping - paginationInfo.offsetLength);
    paginationInfo.offsetLength = lengthAfterShaping;
    paginationInfo.total -= diff;
  }

  return {
    ...paginationInfo,
    [fieldName]: dataField ?? [],
  };
};

const usePagination = (fetcher, routeMap, outerOptions) => {
  const { addInvalidRoute } = useGeneralState();
  const { pathname } = useLocation();

  let innerOptions = {
    range: 10,
    text: "",
    dataKeyName: "data", // will rename the data key to this
    defaultState: {
      data: [],
      pages: 0,
      total: 0,
      offsetLength: 0,
    },
    shapeData: (data) => data,
    base: routeMap?.base ? routeMap.base + "/p" : null,
    refetchOnConditions: [],
    URLParams: {},
    key: null,
    dependencies: [],
    onSuccess: () => {},
  };

  if (outerOptions) {
    const newOpts = safeAssign(innerOptions, outerOptions);
    let newDefaultState = newOpts.defaultState;
    const fieldName = newOpts.dataKeyName;
    if (outerOptions.defaultState) {
      const onShape = newOpts.shapeData;
      const range = newOpts.range;
      newDefaultState = dataShaper(
        outerOptions.defaultState,
        onShape,
        range,
        fieldName,
      );
    } else {
      // remove `data` field and replace it with the fieldName.
      if (fieldName !== "data") {
        newDefaultState = {
          ...newDefaultState,
          [fieldName]: newDefaultState.data ?? [],
        };
        // just in case they are both `data`
        if (fieldName !== "data") {
          delete newDefaultState.data;
        }
      }
    }

    innerOptions = {
      ...innerOptions,
      ...newOpts,
      defaultState: newDefaultState,
    };
  }

  const fieldName = innerOptions.dataKeyName;
  const { page } = useParams();
  let { id } = useParams();

  const [currentPage, setCurrentPage] = useState(() => {
    if (innerOptions.URLParams.hasOwnProperty("page")) {
      return innerOptions.URLParams.page;
    }

    return page ?? 1;
  });

  if (innerOptions.URLParams.hasOwnProperty("id")) {
    id = innerOptions.URLParams.id;
  }

  const [, setCurrentOffset, { current: currentOffset }] = useStateRef(0);

  const {
    data: { pages = 0, total = 0, offsetLength = 0, ...restOfData },
    isLoading,
    ...state
  } = useFetcher(
    async (...args) => {
      let page = currentPage;

      // Just in case I want to pass the page manually
      if (args.length > 0 && args[0].page) {
        page = args[0].page;
      }

      const offset = calculateOffset(page, innerOptions.range);
      setCurrentOffset(offset);
      const payload = await fetcher(offset, ...args);
      return payload;
    },
    {
      ...innerOptions,
      key: stringifer(innerOptions.key, currentPage) + (id ? "/" + id : ""),
      shapeData: (data) => {
        const shapedData = dataShaper(
          data,
          innerOptions.shapeData,
          innerOptions.range,
          fieldName,
        );

        return shapedData;
      },
      onError: (req) => {
        if (req.error === "ABORTED") return;

        if (req) {
          addInvalidRoute({
            name: pathname,
            error: req.status,
          });
        }

        if (innerOptions.onError) innerOptions.onError(req);
      },
    },
  );

  const resetPagination = () => {
    setCurrentPage(1);
    setCurrentOffset(0);
  };

  const PaginationComponent = useMemo(() => {
    return ({ ...props }) =>
      pages > 1 && (
        <Pagination
          {...props}
          pages={pages}
          base={innerOptions.base}
          current={currentPage}
          onPageChange={(page) => {
            setCurrentPage(page);
          }}
          note={
            <>
              <Text className="font-normal text-xs" test-id="vXQ0x_Lj">
                Showing {Math.min(offsetLength, Math.floor(total) || 0)} entries
                of {Math.floor(total) || 0}
                {innerOptions.text && (
                  <Text as="span" test-id="GXNMpHkc">
                    {innerOptions.text}
                  </Text>
                )}
              </Text>
            </>
          }
          test-id="_5bH4OSD"
        />
      );
  }, [
    isLoading,
    pages,
    innerOptions.base,
    currentPage,
    offsetLength,
    total,
    innerOptions.text,
  ]);

  const data = extractNestedKey(restOfData, fieldName, "data") ?? restOfData;

  // for defaultState
  useEffect(() => {
    if (innerOptions.enabled || isUndefined(innerOptions.enabled))
      innerOptions.onSuccess(data);
  }, [data]);

  return [
    PaginationComponent,
    {
      [fieldName]: data,
      isLoading,
      pages,
      offsetLength,
      total,
      ...state,
      currentOffset,
      currentPage,
      setCurrentPage,
      resetPagination,
    },
  ];
};

export default usePagination;
